import React from 'react';
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { LocationOn as LocationOnIcon } from '@mui/icons-material';
import { SafetyContactsPanel } from '../components/SafetyContactsPanel';
import {
  safetyContactsService,
  SafetyContactsLookupResponse,
  SAFETY_CONTACTS_FALLBACK,
} from '../services/safetyContactsService';
import { authService } from '../services/authService';

// FEAT-044 — Local Safety Contacts page (MVP-8.10a, MVP-18.31).
// Calm, never alarming. The Safety entry must read as informative help, not
// as a failure (per work-item tone reminder). Users enter a US 5-digit zip;
// the panel either renders local non-emergency police + APS with tel: links
// or falls back to the national 211 line. Unauthenticated users see the
// national fallback statically — the API endpoint requires Firebase auth.

const ZIP_PATTERN = /^\d{5}$/;

export const SafetyPage: React.FC = () => {
  const theme = useTheme();
  const [zipInput, setZipInput] = React.useState('');
  const [submittedZip, setSubmittedZip] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<SafetyContactsLookupResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isAuthenticated = authService.isAuthenticated();

  const handleLookup = React.useCallback(async (zip: string) => {
    if (!ZIP_PATTERN.test(zip)) {
      setError('Please enter a US 5-digit zip code.');
      setResult(null);
      setSubmittedZip(null);
      return;
    }
    setError(null);
    setSubmittedZip(zip);
    if (!isAuthenticated) {
      setResult(SAFETY_CONTACTS_FALLBACK);
      return;
    }
    setLoading(true);
    try {
      const lookup = await safetyContactsService.lookupByZip(zip);
      setResult(lookup);
    } catch (lookupError) {
      const message =
        lookupError instanceof Error ? lookupError.message : 'Lookup failed';
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void handleLookup(zipInput.trim());
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Safety
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: 1, maxWidth: 640, lineHeight: 1.6 }}
          >
            Find calm, local non-emergency contacts for the area you care for.
            Enter a US zip code below to see the local non-emergency police
            line and Adult Protective Services. If a local mapping isn't
            available yet, we'll show the national United Way 211 line.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          aria-label="Look up local safety contacts by zip code"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            backgroundColor: theme.palette.background.paper,
            p: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <TextField
            label="US zip code"
            value={zipInput}
            onChange={(event) => setZipInput(event.target.value)}
            inputProps={{
              inputMode: 'numeric',
              pattern: '\\d{5}',
              maxLength: 5,
              'aria-label': 'US 5-digit zip code',
              'data-testid': 'safety-zip-input',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnIcon aria-hidden />
                </InputAdornment>
              ),
            }}
            sx={{ flex: '1 1 220px', minWidth: 200 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            data-testid="safety-zip-submit"
          >
            See local contacts
          </Button>
        </Box>

        <SafetyContactsPanel
          zip={submittedZip ?? undefined}
          result={result}
          loading={loading}
          error={error}
        />

        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            For a medical emergency, please call 911. The contacts above are for
            non-emergency situations.
          </Typography>
        </Box>
      </Stack>
    </Container>
  );
};

export default SafetyPage;
